import { useRef, useState } from 'react';
import { api } from '../api/client';
import { ApiError, Category } from '../api/types';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { Icon, PICKABLE_ICONS, categoryIconName } from '../components/Icon';
import { RhythmCards } from '../components/RhythmCards';
import { theme } from '../theme';
import { fmtPlanNum } from '../utils/plan-pad';
import { planDailyLimit, planPerDay } from '../utils/plan-limit';
import { plural } from '../utils/plural';
import { nextSortToEnd } from '../utils/category-order';

const { ink, sub, line, subSoft, paper, red, green } = theme.colors;

function Caption({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        textTransform: 'uppercase',
        letterSpacing: '.18em',
        fontSize: 11,
        color: sub,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Инлайн-редактор категории (C3).
 * Props: category — текущий объект, opsCount — кол-во операций,
 * cats — все категории (для лимита в тостах и nextSortToEnd),
 * onClose — «Готово», удаление, «обнулить».
 */
export function CategoryEditor(props: {
  category: Category;
  opsCount: number;
  /** Все категории — для лимита в тостах и nextSortToEnd. */
  cats: Category[];
  onClose: () => void;
}): JSX.Element {
  const { category, opsCount, cats, onClose } = props;
  const { updateCategory, refresh } = useBudget();
  const toast = useToast();

  // Локальный state имени — коммитится по blur и «Готово»
  const [nameLocal, setNameLocal] = useState(category.name);
  const nameRef = useRef(nameLocal);
  nameRef.current = nameLocal;
  // Guard: значение, которое уже отправлено в PATCH — предотвращает двойной вызов
  // (blur + handleDone оба вызывают commitName; второй будет no-op)
  const committedNameRef = useRef<string | null>(null);

  // Для секции «Группа»
  const knownGroups = Array.from(
    new Set(cats.map((c) => c.grp).filter(Boolean)),
  );
  const [addingGroup, setAddingGroup] = useState(false);
  const [groupInput, setGroupInput] = useState('');
  // Guard: не дать Enter и следующему blur оба дёрнуть updateCategory
  const groupCommittedRef = useRef(false);

  // Коммит переименования — guard не даёт отправить дважды одно и то же значение
  async function commitName() {
    const nm = nameRef.current.trim();
    if (!nm) {
      setNameLocal(category.name);
      toast.show('имя не может быть пустым', 'error');
      return;
    }
    if (nm !== category.name) {
      if (committedNameRef.current === nm) return; // уже отправлено
      committedNameRef.current = nm;
      try {
        await updateCategory({ name: category.name, newName: nm });
        // Единственная мутация, которую нельзя применить локально: имя категории
        // — внешний ключ операций, резервов и правил импорта, каскад делает
        // сервер. Поэтому сверяемся явно, как при удалении категории ниже.
        await refresh();
      } catch (e) {
        committedNameRef.current = null; // сбросить guard при ошибке
        if (e instanceof ApiError) toast.show(e.message, 'error');
        else toast.show('Ошибка при переименовании', 'error');
      }
    }
  }

  // Смена ритма (optimistic): toast немедленный, ошибка через .catch()
  function handleRhythmChange(v: boolean) {
    const nm = nameRef.current.trim() || category.name;
    const patchedCats = cats.map((c) =>
      c.id === category.id ? { ...c, is_daily: v ? 1 : 0 } : c,
    );
    const newLimit = planDailyLimit(patchedCats);
    updateCategory({ name: category.name, is_daily: v }).catch((e) => {
      if (e instanceof ApiError) toast.show(e.message, 'error');
      else toast.show('Ошибка', 'error');
    });
    toast.reward({
      title: nm + (v ? ' — теперь повседневная' : ' — теперь конверт месяца'),
      coin: false,
      sub: `дневной лимит ≈${fmtPlanNum(newLimit)} ₽/день`,
    });
  }

  // Квест-штамп
  function handleHotToggle() {
    updateCategory({ name: category.name, is_hot: !category.is_hot }).catch((e) => {
      if (e instanceof ApiError) toast.show(e.message, 'error');
      else toast.show('Ошибка', 'error');
    });
  }

  // «Готово»
  async function handleDone() {
    await commitName();
    onClose();
  }

  // Обнулить план и убрать вниз
  async function handleReset() {
    const nm = category.name;
    try {
      await updateCategory({
        name: nm,
        plan_month: 0,
        sort_order: nextSortToEnd(cats, category.type),
      });
      toast.reward({
        title: nm + ' — план 0',
        coin: false,
        sub: 'не влияет на лимит, ушла из частых чипов; история цела',
      });
      onClose();
    } catch (e) {
      if (e instanceof ApiError) toast.show(e.message, 'error');
      else toast.show('Ошибка', 'error');
    }
  }

  // Удалить (только при opsCount === 0)
  async function handleDelete() {
    try {
      await api.deleteCategory(category.name);
      await refresh();
      toast.reward({
        title: `Удалена «${category.name}»`,
        coin: false,
        sub: 'операций не было',
      });
      onClose();
    } catch (e) {
      if (e instanceof ApiError) toast.show(e.message, 'error');
      else toast.show('Ошибка при удалении', 'error');
    }
  }

  const isExpense = category.type === 'Расход';
  const isHot = Boolean(category.is_hot);

  return (
    <div
      className="rise"
      style={{
        border: `1px solid ${ink}`,
        borderRadius: theme.radius.sm,
        padding: 14,
        margin: '10px 0',
      }}
    >
      {/* 1. Caption типа */}
      <Caption>
        {isExpense ? 'расход' : 'доход'} · тип задан при создании
      </Caption>

      {/* 2. Имя */}
      <input
        value={nameLocal}
        onChange={(e) => setNameLocal(e.target.value)}
        onBlur={commitName}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          marginTop: 8,
          padding: '9px 11px',
          fontSize: 16,
          border: `1px solid ${line}`,
          borderRadius: theme.radius.sm,
          background: theme.colors.bg,
          fontFamily: theme.font,
          color: ink,
          outline: 'none',
        }}
      />
      <div style={{ fontSize: 11.5, color: sub, fontStyle: 'italic', marginTop: 4 }}>
        переименование безопасно — операции и правила переедут сами
      </div>

      {/* 3. Ритм траты (только Расход) */}
      {isExpense && (
        <>
          <Caption>
            <span style={{ display: 'block', marginTop: 14 }}>ритм траты</span>
          </Caption>
          <RhythmCards
            daily={Boolean(category.is_daily)}
            perDay={planPerDay(category.plan_month)}
            onChange={handleRhythmChange}
          />
        </>
      )}

      {/* 4. Квест-штамп (только Расход) */}
      {isExpense && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 14,
          }}
        >
          <button
            type="button"
            onClick={handleHotToggle}
            style={{
              background: 'transparent',
              border: isHot
                ? `1.5px solid ${green}`
                : `1px dashed ${subSoft}`,
              color: isHot ? green : sub,
              transform: 'rotate(-3deg)',
              fontSize: 10,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              padding: '3px 8px',
              borderRadius: 0,
              cursor: 'pointer',
              fontFamily: theme.font,
            }}
          >
            {isHot ? 'в квестах и стриках' : 'добавить в квесты'}
          </button>
          <span style={{ fontSize: 11.5, color: sub, fontStyle: 'italic' }}>
            «дни без» и недельные квесты
          </span>
        </div>
      )}

      {/* 5. Значок */}
      <div style={{ marginTop: 14 }}>
        <Caption>значок</Caption>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 6,
            marginTop: 8,
          }}
        >
          {PICKABLE_ICONS.map((ic) => {
            const selected = categoryIconName(category) === ic;
            return (
              <button
                key={ic}
                type="button"
                onClick={() => updateCategory({ name: category.name, icon: ic }).catch((e) => {
                  if (e instanceof ApiError) toast.show(e.message, 'error');
                })}
                style={{
                  aspectRatio: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: selected ? ink : 'transparent',
                  color: selected ? paper : ink,
                  border: `1px solid ${selected ? ink : line}`,
                  borderRadius: theme.radius.sm,
                  cursor: 'pointer',
                }}
              >
                <Icon name={ic} size={17} />
              </button>
            );
          })}
        </div>
      </div>

      {/* 6. Цвет */}
      <div style={{ marginTop: 14 }}>
        <Caption>цвет</Caption>
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {[...theme.colors.inkTints, theme.colors.green, theme.colors.red].map(
            (c) => {
              const selected = category.color === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() =>
                    updateCategory({ name: category.name, color: c }).catch((e) => {
                      if (e instanceof ApiError) toast.show(e.message, 'error');
                    })
                  }
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: theme.radius.sm,
                    background: c,
                    border: selected
                      ? `1.5px solid ${ink}`
                      : `1px solid ${line}`,
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0,
                  }}
                />
              );
            },
          )}
        </div>
      </div>

      {/* 7. Группа */}
      <div style={{ marginTop: 14 }}>
        <Caption>группа для графиков</Caption>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {knownGroups.map((g) => {
            const selected = category.grp === g;
            return (
              <button
                key={g}
                type="button"
                onClick={() =>
                  updateCategory({
                    name: category.name,
                    grp: selected ? '' : g,
                  }).catch((e) => {
                    if (e instanceof ApiError) toast.show(e.message, 'error');
                  })
                }
                style={{
                  padding: '7px 13px',
                  fontSize: 13,
                  borderRadius: 999,
                  border: `1px solid ${selected ? ink : line}`,
                  background: selected ? ink : 'transparent',
                  color: selected ? paper : ink,
                  cursor: 'pointer',
                  fontFamily: theme.font,
                }}
              >
                {g}
              </button>
            );
          })}

          {/* Чип «+ новая» или инлайн-input */}
          {addingGroup ? (
            <input
              autoFocus
              value={groupInput}
              onChange={(e) => setGroupInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const v = groupInput.trim();
                  if (v && !groupCommittedRef.current) {
                    groupCommittedRef.current = true;
                    updateCategory({ name: category.name, grp: v }).catch((err) => {
                      if (err instanceof ApiError) toast.show(err.message, 'error');
                    });
                  }
                  setAddingGroup(false);
                  setGroupInput('');
                } else if (e.key === 'Escape') {
                  groupCommittedRef.current = true; // Escape — не коммитить при blur
                  setAddingGroup(false);
                  setGroupInput('');
                }
              }}
              onBlur={() => {
                const v = groupInput.trim();
                if (v && !groupCommittedRef.current) {
                  updateCategory({ name: category.name, grp: v }).catch((err) => {
                    if (err instanceof ApiError) toast.show(err.message, 'error');
                  });
                }
                groupCommittedRef.current = false;
                setAddingGroup(false);
                setGroupInput('');
              }}
              style={{
                fontSize: 13,
                width: 140,
                padding: '6px 10px',
                border: `1px solid ${line}`,
                borderRadius: theme.radius.sm,
                background: theme.colors.bg,
                fontFamily: theme.font,
                color: ink,
                outline: 'none',
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingGroup(true)}
              style={{
                padding: '7px 13px',
                fontSize: 13,
                borderRadius: 999,
                border: `1px dashed ${subSoft}`,
                background: 'transparent',
                color: sub,
                cursor: 'pointer',
                fontFamily: theme.font,
              }}
            >
              + новая
            </button>
          )}
        </div>
      </div>

      {/* 8. Удаление */}
      <div
        style={{
          borderTop: `1px solid ${line}`,
          marginTop: 14,
          paddingTop: 12,
        }}
      >
        {opsCount > 0 ? (
          <>
            <div style={{ fontSize: 13, color: sub }}>
              удалить нельзя —{' '}
              <span style={{ color: ink, fontVariantNumeric: 'tabular-nums' }}>
                {fmtPlanNum(opsCount)}{' '}
                {plural(opsCount, ['операция', 'операции', 'операций'])}
              </span>{' '}
              в истории
            </div>
            <button
              type="button"
              onClick={handleReset}
              style={{
                marginTop: 8,
                padding: '9px 14px',
                background: 'transparent',
                color: sub,
                border: `1px solid ${line}`,
                borderRadius: theme.radius.sm,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: theme.font,
              }}
            >
              Обнулить план и убрать вниз
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={handleDelete}
            style={{
              background: 'transparent',
              border: 'none',
              color: red,
              fontSize: 13,
              borderBottom: `1px solid ${red}`,
              padding: '0 2px 1px',
              cursor: 'pointer',
              fontFamily: theme.font,
            }}
          >
            Удалить категорию
          </button>
        )}
      </div>

      {/* 9. «Готово» */}
      <button
        type="button"
        onClick={handleDone}
        style={{
          width: '100%',
          marginTop: 12,
          padding: '11px 0',
          background: ink,
          color: paper,
          border: 'none',
          borderRadius: theme.radius.sm,
          fontFamily: theme.fontDisplay,
          fontSize: 15,
          cursor: 'pointer',
        }}
      >
        Готово
      </button>
    </div>
  );
}
